import { Dayjs } from 'dayjs';
import { Qualification } from './jobQualification';
import { Address, RegionInfo, WeekDay } from '../common';
import { LocationTiny } from '../locations/location';
import { SpecialtyShort } from './specialty';

export const STAFFING_TYPES = {
  INTERNAL: 'Internal',
  AGENCY: 'Agency',
  DIRECT_CONTRACTOR: 'DirectContractor',
} as const;

export const STAFFING_TYPES_LIST = Object.values(STAFFING_TYPES);
export type StaffingType = (typeof STAFFING_TYPES)[keyof typeof STAFFING_TYPES];

export const EMPLOYMENT_SHORT_TYPES = {
  FULL_TIME: 'FullTime',
  PART_TIME: 'PartTime',
} as const;

export const EMPLOYMENT_SHORT_TYPES_LIST = Object.values(EMPLOYMENT_SHORT_TYPES);
export type EmploymentTypeShort = (typeof EMPLOYMENT_SHORT_TYPES)[keyof typeof EMPLOYMENT_SHORT_TYPES];

export const EMPLOYMENT_TYPES = {
  CONTRACT: 'Contract',
  SALARIED_PART_TIME: 'SalariedPartTime',
  SALARIED_FULL_TIME: 'SalariedFullTime',
  HOURLY_PART_TIME: 'HourlyPartTime',
  HOURLY_FULL_TIME: 'HourlyFullTime',
  PER_DIEM_OR_PRN: 'PerDiemOrPRN',
} as const;

export const EMPLOYMENT_TYPES_LIST = Object.values(EMPLOYMENT_TYPES);
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[keyof typeof EMPLOYMENT_TYPES];

export const RATE_TYPES = {
  HOURLY: 'Hourly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
} as const;

export const RATE_TYPES_LIST = Object.values(RATE_TYPES);
export type RateType = (typeof RATE_TYPES)[keyof typeof RATE_TYPES];

export const CONTRACT_TYPES = {
  W2: 'W2',
  TEN_NINETY_NINE: 'TenNinetyNine',
} as const;

export const CONTRACT_TYPES_LIST = Object.values(CONTRACT_TYPES);
export type ContractType = (typeof CONTRACT_TYPES)[keyof typeof CONTRACT_TYPES];

export const STAFFING_STATUSES = {
  ACTIVE: 'Active',
  DEACTIVATED: 'Deactivated',
} as const;

export const STAFFING_STATUSES_LIST = Object.values(STAFFING_STATUSES);
export type StaffingStatus = (typeof STAFFING_STATUSES)[keyof typeof STAFFING_STATUSES];

export const FILTRABLE_STAFFING_STATUSES = STAFFING_STATUSES;
export const FILTRABLE_STAFFING_STATUSES_LIST = Object.values(FILTRABLE_STAFFING_STATUSES);

export type StaffTinyInfo = {
  id: string; //'staffmember-01k2sn9pvwm0aw436fqb177fxh';
  firstName: string; //  'Carey';
  lastName: string; //  'Howell';
  fullName: string; //  'Carey Howell';
};

export type StaffShortInfo = StaffTinyInfo & {
  jobTitle: string;
  imageUrl?: string | null;
};

export type StaffListItem = StaffTinyInfo & {
  staffingType: StaffingType;
  employmentType: EmploymentType;
  imageUrl?: string;
  phoneNumber: string;
  email: string;
  address: Address;
  status: StaffingStatus;
  qualifications: Qualification[];
  specialties: SpecialtyShort[];
  lastWorkedTime: Dayjs;
  credentials: StaffCredential[];
};

export type StaffDetails = StaffTinyInfo & {
  imageUrl: string; // 'https://picsum.photos/640/480/?image=553';
  phoneNumber: string; //  '+1 (158) 629-4212';
  email: string; //  'Antonio_Witting60@gmail.com';
  address: Address;
  hrInfo: {
    primaryRegion: RegionInfo;
    secondaryRegions: RegionInfo[];
  };
  jobTitle: string; //  'Central Interactions Assistant';
  location: LocationTiny;
  qualificationPayRates: {
    qualification: Qualification;
    rateType: RateType;
    rate: number;
    specialtyPayRates: {
      specialty: SpecialtyShort;
      rate: number;
    }[];
  }[];
  employmentType: EmploymentType;
  availableForTravelDistance: number;
  workShifts: {
    dayOfWeek: WeekDay;
    startTime: string; //  '08:00:00';
    duration: string; //  '08:00:00';
  }[];
  status: StaffingStatus;
  lastWorkedTime: string; //  '2025-08-16T14:42:19.6453854Z';
  contractType: ContractType;
  credentials: StaffCredential[];
};

export const CREDENTIALS_TYPES = {
  LICENSE: 'License',
  CERTIFICATION: 'Certification',
  BACKGROUND_CHECK: 'BackgroundCheck',
  HEALTH_RECORD: 'HealthRecord',
  ASSESSMENT: 'Assessment',
  E_DOCUMENT: 'EDocument',
} as const;

export const CREDENTIALS_TYPES_LIST = Object.values(CREDENTIALS_TYPES);
export type CredentialsType = (typeof CREDENTIALS_TYPES)[keyof typeof CREDENTIALS_TYPES];

export type StaffCredential = {
  type: CredentialsType;
  name: string; //  'File Name.pdf'; - any human-readable name (not the physical file name)
  url: string; //  'https://example.com/file.pdf';
};

export type StaffEditData = Omit<StaffDetails, 'fullName' | 'imageUrl'> & {
  image: File | null;
  isImageUrlNeedToBeUpdated: boolean;
};

export type StaffCreateData = Omit<StaffDetails, 'id' | 'lastWorkedTime' | 'fullName' | 'imageUrl' | 'status'> & {
  image: File | null;
  isImageUrlNeedToBeUpdated: boolean;
};

export class StaffHasActiveShiftsError extends Error {
  data: StaffHasActiveShiftsErrorDetails;

  constructor(data: StaffHasActiveShiftsErrorDetails) {
    super('Staff has active shifts');
    this.name = 'StaffHasActiveShiftsError';
    this.data = data;
  }
}

export type StaffHasActiveShiftsErrorDetails = {
  detail: string;
  instance: string;
  staff: {
    fullName: string;
    staffId: string;
  }[];
  status: 403;
  title: 'staffHasActiveShifts';
  traceId: string;
  type: 'staffHasActiveShifts';
};
